'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { Brand } from '@/domainLogic/index.ts';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useSearchPageWrapperData } from '@/app/components/search/searchPageCommonWrapperProvider.tsx';
import { type SearchCommonPageData } from '@/app/components/search/SearchMain.tsx';
import { type SearchParams } from '@/server/searchParams.ts';
import { useSearchDefaultData } from '@/app/components/search/searchDefaultDataProvider.tsx';
import DebugBanner from '@/app/components/search/components/Banners/Debug/DebugBanner.tsx';
import {
  FILTER_SURFACE,
  getCustomFilterMainCreators,
} from '@/app/common/filters/surfaces/forms/custom/customFilterMainCreators.ts';
import { createSearchParamsAccommodationFilter } from '@/app/components/search/filters/createSearchParamsAccommodationFilter.ts';
import HorizontalLayout from '@/app/common/filters/surfaces/bar/HorizontalLayout';
import { getFiltersModule } from '@/app/common/filters/variants/evo/filtersModule.ts';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import FloatingLayout from '@/app/common/filters/surfaces/bar/floating/FloatingLayout.tsx';
import { createFloatingPillsIndex } from '@/app/common/filters/surfaces/bar/floating/createFloatingPills.ts';
import { FiltersBarContainerMobile } from '@/app/components/search/components/SearchPageContainer/ListContainer/styled.ts';
import { useFilterBarLoading } from '@/app/common/filters/surfaces/provider/useFilterBarLoading.ts';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import { evoFiltersEnabled } from '@/app/common/filters/core/filterFlags.ts';
import { watchSearchLoading } from '@/app/components/search/logging/searchLoadingWatchdog.ts';
import ErrorBoundary from '@/app/common/components/ErrorBoundary';
import { RouteType } from '../Curiosity';
import { useFlex } from '../hooks/useFlex';
import { type HandleReloadDealsParams, ReloadType } from '../utils';
import { resetProducts } from '../common/resetProducts';
import { PATTERN_LIMIT } from '../Utilities';
import { initBiffSearchPage } from '../../../common/biff/searchPage/biffSearchPage.ts';
import { BIFF_CONFIGURATIONS } from '../../../common/biff/searchPage/typesSearchPage.ts';
import { logClientSideTimings } from '../utils/timings/timingsClient';
import { Main } from '../Index/styled';
import Adv from '../../../common/biff/searchPage/AdvSearchPage';
import Title from '../components/Title';
import CityCardsGrid from '../components/CityCardsGrid';
import ShowMore from '../components/ShowMore';
import showMoreClick from '../common/showMoreClick';
import type { CityCard } from '../utils/buildCityCards/cityCardTypes';
import { showMoreToggle } from '../components/ShowMore/utils';
import { type HandleFiltersChanged, type HandleSortChanged } from '../components/SearchPageContainer';
import { removeAllFilters } from '../components/Filters/utils';
import { sumClasses } from '../utils/misc';
import { SearchCommonPageContext } from '../searchCommonPageProvider.tsx';
import useIsClient from '../hooks/useIsClient';

export type GoToRoute = (newSearchParams: SearchParams) => void;

const NoResults = dynamic(() => import('../components/NoResults'), { ssr: false });

const IndexClient = () => {
  const {
    searchInfoData,
    searchInfoDetailData,
    localization,
    curiosityParams,
    commonData,
    searchParams: pageSearchParams,
  } = usePageWrapperData();
  const { setIsLoading, handleSortChangedCommon, filtersGroups, handleFilterChanged, filterOrder } =
    useSearchPageWrapperData();
  const { t } = useTranslation();
  const { defaultValues, defaultDestItems } = useSearchDefaultData();
  const { productsData, isLoading } = useSearchPageData();
  const { starTrack, handleNewSearch, handleSearchProductsDataChanged } = useCommonClient();
  const { authResponse } = commonData;
  const { searchId } = searchInfoData;
  const { cityCards, productCounters } = productsData;
  const useEvoFilters = evoFiltersEnabled(localization?.configValuesApp);
  const { createPills } = getFiltersModule(useEvoFilters);
  const customFilterMainCreators = getCustomFilterMainCreators(useEvoFilters, FILTER_SURFACE.PILL_OVERLAY);
  const barLoading = useFilterBarLoading(useEvoFilters);
  const floatingLoading = useFilterBarLoading();
  const brand = localization?.brand;
  const mainRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const cardsGridRef = useRef<HTMLDivElement>(undefined);
  const disasterRecovery = localization?.configValuesApp?.disasterRecovery;
  const showResults = !disasterRecovery;
  const isClient = useIsClient();
  const routeType = RouteType.SEARCHRESULTS;
  const { isMobile } = useCommonClient();

  useFlex();

  const setProductsData = useCallback(
    (newProductsData: ProductsData) => {
      handleSearchProductsDataChanged(newProductsData);
    },
    [handleSearchProductsDataChanged]
  );

  const goToRoute = useCallback(
    (newSearchParams: SearchParams) => {
      handleNewSearch(newSearchParams);
    },
    [handleNewSearch]
  );

  const handleAfterHaveResponse = useCallback(
    (productsDataAfterUpdate: ProductsData) => {
      setProductsData(productsDataAfterUpdate);
    },
    [setProductsData]
  );

  const handleReloadDeals = useCallback(
    async (handleReloadDealsParams: HandleReloadDealsParams) => {
      await resetProducts({
        setIsLoading,
        showMoreToggle,
        setOffset,
        curiosityParams,
        localization,
        defaultDestItems,
        goToRoute,
        starTrack,
        searchInfoData,
        searchInfoDetailData,
        commonData,
        handleReloadDealsParams,
        handleAfterHaveResponse,
      });

      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [
      commonData,
      curiosityParams,
      defaultDestItems,
      goToRoute,
      handleAfterHaveResponse,
      localization,
      searchInfoData,
      searchInfoDetailData,
      setIsLoading,
      starTrack,
    ]
  );

  const handleSortChanged = useCallback(
    ({ newSortValue, searchParamsUpdated }: HandleSortChanged) => {
      const { searchParamsNormalised } = handleSortChangedCommon({ newSortValue, searchParamsUpdated });
      handleReloadDeals({ searchParams: searchParamsNormalised, reloadType: ReloadType.SORT });
    },
    [handleReloadDeals, handleSortChangedCommon]
  );

  const handleFiltersChanged = useCallback(
    ({ searchParams, appliedFilters }: HandleFiltersChanged) => {
      const searchParamsAccommodationFilter = createSearchParamsAccommodationFilter({ searchParams, appliedFilters });
      const appliedSearchParams = { ...removeAllFilters(searchParamsAccommodationFilter), ...appliedFilters };
      handleReloadDeals({ searchParams: appliedSearchParams, reloadType: ReloadType.FILTER });
    },
    [handleReloadDeals]
  );

  const handleShowMoreResultsReady = (cityCardsMore: CityCard[]) => {
    const prevCityCardsUpdated =
      productsData?.cityCards?.map((prevCityCard): CityCard => ({ ...prevCityCard, appending: false })) || [];
    setProductsData({ ...productsData, cityCards: [...prevCityCardsUpdated, ...cityCardsMore] });
  };

  useEffect(() => {
    setOffset(0);
  }, [searchInfoData]);

  useEffect(() => {
    if (canUseDOM) {
      showMoreToggle(cityCards?.length === PATTERN_LIMIT);

      logClientSideTimings({ commonData, localization, curiosityParams, searchInfoData });
    }
  }, [
    cityCards?.length,
    commonData,
    curiosityParams,
    defaultDestItems,
    defaultValues,
    localization,
    productCounters?.productsCount,
    searchId,
    searchInfoData,
    searchInfoDetailData,
  ]);

  // Mount-only on purpose: one configuration request per page visit. `fromIndex` has to be read once and
  // before the destinations visit event is pushed (this child effect runs ahead of the PageWrapperClient
  // effect that fires analyticsResultsEvent) — read again on a later run it would sit past that event and
  // the wait could never match. The abort keeps a load still queued on consent from installing the
  // destinations slots over whichever page the user has moved on to.
  useEffect(() => {
    if (!canUseDOM || brand === Brand.HOLIDAYPIRATES) return undefined;

    const abortController = new AbortController();

    initBiffSearchPage({
      configuration: BIFF_CONFIGURATIONS.MIDDLE_PAGE,
      isLoggedIn: !!authResponse?.loggedIn,
      fromIndex: window.platformData?.length ?? 0,
      signal: abortController.signal,
    });

    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Report a loading state that never ends: this is the flag that keeps the destinations placeholders up.
  useEffect(() => {
    watchSearchLoading(isLoading);
  }, [isLoading]);

  const mainClassName = sumClasses([isLoading ? 'openx-ui-main-loading' : '', 'openx-ui-main-index']);

  const hasNoResults = (cityCards?.length || 0) === 0;

  const resultsNotOver = (cityCards?.length || 0) < (productCounters?.productsCount || 0);

  const searchCommonPageContextValue: SearchCommonPageData = useMemo(
    () => ({
      handleReloadDeals,
      handleSortChanged,
      handleFiltersChanged,
      handleMapChanged: () => {},
    }),
    [handleReloadDeals, handleSortChanged, handleFiltersChanged]
  );

  const filtersLayout = () => {
    if (isMobile && isClient) {
      return (
        <FloatingLayout
          filterPills={createFloatingPillsIndex()}
          customFilterMainCreators={customFilterMainCreators}
          isLoading={floatingLoading.isLoading}
          handleFilterChanged={handleFilterChanged}
        />
      );
    }

    const filterPills = createPills({
      filtersGroups,
      searchInfoData,
      productsData,
      t,
      searchParams: pageSearchParams,
      filterOrder,
    });

    return (
      <HorizontalLayout
        filterPills={filterPills}
        customFilterMainCreators={customFilterMainCreators}
        isLoading={barLoading.isLoading}
        handleFilterChanged={handleFilterChanged}
        scrollToStartOnChange={useEvoFilters}
        isIndex={true}
      />
    );
  };

  return (
    <SearchCommonPageContext.Provider value={searchCommonPageContextValue}>
      <Main ref={mainRef} className={mainClassName}>
        <ErrorBoundary id="Results/DebugBanner">
          <DebugBanner />
        </ErrorBoundary>
        {showResults && (
          <>
            <ErrorBoundary id="Title">
              <Title isIndex={true} isWaiting={isLoading} />
            </ErrorBoundary>
            <ErrorBoundary id="Destinations/FilterBar">{filtersLayout()}</ErrorBoundary>
            {isClient && (
              <ErrorBoundary id="NoResults">
                <NoResults hasNoResults={hasNoResults} isLoading={isLoading} isWaiting={false} />
              </ErrorBoundary>
            )}
            <CityCardsGrid
              cityCards={cityCards || []}
              routeType={routeType}
              cardsGridRef={cardsGridRef}
              goToRoute={goToRoute}
              hide={hasNoResults}
            />
            {!hasNoResults && resultsNotOver && (
              <ShowMore
                handleOnClick={() =>
                  showMoreClick({
                    cityCards: cityCards || [],
                    mainRef,
                    offset,
                    showMoreToggle,
                    setOffset,
                    curiosityParams,
                    commonData,
                    localization,
                    searchInfoData,
                    searchInfoDetailData,
                    handleShowMoreResultsReady,
                  })
                }
              />
            )}
            {isMobile && <FiltersBarContainerMobile className="filters-bar-container" />}
          </>
        )}
        <Adv id="div-gpt-ad-bottom-0" isBottom={true} hide={hasNoResults} />
        <Adv id="div-gpt-ad-middle-3" id2="div-gpt-ad-middle-2" isDouble={true} hide={true} />
      </Main>
    </SearchCommonPageContext.Provider>
  );
};

export default IndexClient;
